import { VALIDATION_RULES, MESSAGES } from '../constants/taskTemplatesConstants';
import { formatDate } from '../../../../utils/formatUtils';

/**
 * Formatea una fecha para mostrar en formato DD/MM/YYYY
 * @param {string} dateString - Fecha en formato ISO
 * @returns {string} - Fecha formateada
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '-';

  try {
    return formatDate(dateString);
  } catch (error) {
    return '-';
  }
};

/**
 * Valida los datos de una plantilla de tareas
 * @param {Object} templateData - Datos de la plantilla a validar
 * @returns {Object} - Objeto con errores de validación
 */
export const validateTemplateData = (templateData) => {
  const errors = {};

  // Validar nombre
  if (!templateData.name || !templateData.name.trim()) {
    errors.name = MESSAGES.ERROR.REQUIRED_FIELD;
  } else if (templateData.name.trim().length < VALIDATION_RULES.NAME.MIN_LENGTH) {
    errors.name = MESSAGES.ERROR.NAME_TOO_SHORT;
  } else if (templateData.name.trim().length > VALIDATION_RULES.NAME.MAX_LENGTH) {
    errors.name = MESSAGES.ERROR.NAME_TOO_LONG;
  }

  // Validar descripción (opcional pero con límite)
  if (
    templateData.description &&
    templateData.description.length > VALIDATION_RULES.DESCRIPTION.MAX_LENGTH
  ) {
    errors.description = MESSAGES.ERROR.DESCRIPTION_TOO_LONG;
  }

  // Validar tipo de plantilla
  if (!templateData.template_type) {
    errors.template_type = MESSAGES.ERROR.REQUIRED_FIELD;
  } else if (!['progress', 'quality'].includes(templateData.template_type)) {
    errors.template_type = MESSAGES.ERROR.INVALID_TYPE;
  }

  return errors;
};

/**
 * Prepara los datos de la plantilla para envío a la API
 * @param {Object} templateData - Datos de la plantilla
 * @returns {Object} - Datos preparados para la API
 */
export const prepareTemplateData = (templateData) => {
  const cleanedData = { ...templateData };

  // Limpiar campos vacíos y espacios innecesarios
  Object.keys(cleanedData).forEach((key) => {
    if (typeof cleanedData[key] === 'string') {
      cleanedData[key] = cleanedData[key].trim();
      if (cleanedData[key] === '') {
        cleanedData[key] = null;
      }
    }
  });

  // Remover campos que no deben enviarse
  delete cleanedData.uuid;
  delete cleanedData.task_count;
  delete cleanedData.created_at;
  delete cleanedData.updated_at;

  return cleanedData;
};

/**
 * Filtra plantillas por término de búsqueda
 * @param {Array} templates - Lista de plantillas
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array} - Plantillas filtradas
 */
export const filterTemplatesBySearch = (templates, searchTerm) => {
  if (!searchTerm.trim()) {
    return templates;
  }

  const term = searchTerm.toLowerCase();
  return templates.filter(
    (template) =>
      template.name?.toLowerCase().includes(term) ||
      template.description?.toLowerCase().includes(term)
  );
};

/**
 * Ordena plantillas por criterio específico
 * @param {Array} templates - Lista de plantillas
 * @param {string} sortBy - Campo por el cual ordenar
 * @param {string} sortOrder - Orden ('asc' o 'desc')
 * @returns {Array} - Plantillas ordenadas
 */
export const sortTemplates = (templates, sortBy = 'created_at', sortOrder = 'desc') => {
  return [...templates].sort((a, b) => {
    let valueA = a[sortBy];
    let valueB = b[sortBy];

    // Manejar fechas
    if (sortBy.includes('_at')) {
      valueA = new Date(valueA);
      valueB = new Date(valueB);
    }

    // Manejar strings
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
    } else {
      return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
    }
  });
};

/**
 * Obtiene estadísticas de una plantilla
 * @param {Object} template - Plantilla de tareas
 * @returns {Object} - Estadísticas calculadas
 */
export const getTemplateStats = (template) => {
  return {
    taskCount: template.task_count || 0,
    hasDescription: !!template.description,
    isNew:
      template.created_at &&
      new Date(template.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Últimos 7 días
    typeLabel: template.template_type === 'progress' ? 'Progreso' : 'Calidad',
  };
};
